import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { vars } from "~/components/styles/theme.css"

export const meta: MetaFunction = () => {
  return [
    {
      title: "Features of Japanese Zen Gardens | Minimalist Meditation",
    },
    {
      name: "description",
      content:
        "Explore the essential features of Japanese Zen gardens. Learn about rocks, raked sand, moss, and other elements that create serene spaces.",
    },
    {
      property: "og:title",
      content: "Features of Japanese Zen Gardens | Minimalist Meditation",
    },
    {
      property: "og:description",
      content:
        "Explore the essential features of Japanese Zen gardens. Learn about rocks, raked sand, moss, and other elements that create serene spaces.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/guide/zen-5.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide/zen/features-of-zen-gardens",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Features of Japanese Zen Gardens | Minimalist Meditation",
        description:
          "Explore the essential features of Japanese Zen gardens. Learn about rocks, raked sand, moss, and other elements that create serene spaces.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-20",
        dateModified: "2025-10-20",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/guide/zen-5.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/garden-design-guide/zen/features-of-zen-gardens",
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/zen-5.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Features of Japanese Zen Gardens</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Japanese Zen gardens <strong className={bold}>(karesansui)</strong> are
            tranquil spaces designed for reflection, meditation, and mindfulness. They use
            minimalist elements like rocks, gravel, moss, and carefully chosen plants to
            create harmony and serenity.
          </p>
          <figure>
            <img
              src={"/guide/zen-2.webp"}
              alt={"Zen Garden Idea Generated With Rescape AI"}
              loading="lazy"
              className={articleImageStyle}
              style={{ maxHeight: "400px", objectFit: "cover", objectPosition: "0% 75%" }}
            />
            <figcaption className={figCaptionStyle}>
              A Serene, Calm, Mossy Garden Idea Generated on Rescape AI,{" "}
              <Link href="/garden-design-guide/zen/zen-garden-ideas-using-ai">
                See more examples.
              </Link>
            </figcaption>
          </figure>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Key Features</h2>
          <ul className={unorderedList}>
            <li>
              <strong className={bold}>Simplicity and Minimalism:</strong> Every element
              has a purpose. Minimalist design helps create a calm, clutter-free space.
            </li>
            <li>
              <strong className={bold}>Raked Gravel or Sand:</strong> Patterns symbolize
              water or waves and promote mindfulness. Raking is both decorative and
              meditative.
            </li>
            <li>
              <strong className={bold}>Rocks and Stones:</strong> Represent mountains or
              islands. Placement is asymmetrical for natural balance.
            </li>
            <li>
              <strong className={bold}>Moss and Low-Growing Plants:</strong> Adds
              softness, subtle greenery, and seasonal beauty without overwhelming the
              space.
            </li>
            <li>
              <strong className={bold}>Water Elements:</strong> Real or symbolic water
              represents purity and calm. Sand patterns can mimic flowing streams or
              oceans.
            </li>
            <li>
              <strong className={bold}>Bridges and Pathways:</strong> Stepping stones or
              small bridges guide visitors slowly, reinforcing mindfulness.
            </li>
            <li>
              <strong className={bold}>Enclosures and Walls:</strong> Privacy and
              seclusion enhance reflection and create a meditative environment.
            </li>
            <li>
              <strong className={bold}>Seasonal Awareness:</strong> Gardens subtly reflect
              seasonal changes, highlighting the Zen concept of impermanence.
            </li>
          </ul>
          <img
            src={"/guide/raked-sand.webp"}
            alt={"Zen Garden With Raked Sand"}
            loading="lazy"
            className={articleImageStyle}
            style={{ marginTop: "0px" }}
          />
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Why Zen Gardens Are Unique</h2>
          <p className={articleBodyStyle}>
            Unlike traditional gardens, Zen gardens emphasize symbolism over extravagance.
            Rocks, sand, and minimal plantings create an environment for calm thought,
            meditation, and inner peace.
          </p>
          <h2 className={h2Style}>Quick Tips for Creating Your Own Zen Garden</h2>
          <ul className={unorderedList}>
            <li>Use gravel or sand for texture and symbolism.</li>
            <li>Choose a few focal stones instead of many small rocks.</li>
            <li>Incorporate moss or low-growing plants for softness.</li>
            <li>Include pathways or stepping stones for slow movement and reflection.</li>
            <li>Keep the space simple and uncluttered.</li>
            <li>
              <Link href="/hello/signup">Visualise how your garden would look</Link> as a
              zen garden with Rescape AI.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>FAQ: Japanese Zen Gardens</h2>
          <div className={faqGrid}>
            <div>
              <h3 className={h3Style}>
                Why do Zen gardens use sand or gravel instead of water?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                In Zen gardens, sand or gravel symbolizes water. Raked patterns can
                represent flowing rivers, ripples, or ocean waves. This dry landscape
                approach encourages contemplation and allows caretakers to practice
                mindfulness through raking.
              </p>
            </div>
            <div>
              <h3 className={h3Style}>Can a Zen garden include plants?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes. While plants are used sparingly, elements like moss, low-growing
                shrubs, and small trees add subtle beauty and seasonal texture. These
                plants emphasize natural change and complement the garden’s peaceful
                atmosphere.
              </p>
            </div>
            <div>
              <h3 className={h3Style}>Can I create a Zen garden at home?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Absolutely. Even a small space can become a Zen-inspired retreat. Start
                with gravel or sand,{" "}
                <Link href="/garden-design-guide/zen/materials-and-plants">
                  choose a few meaningful stones, incorporate small plants like moss,
                </Link>{" "}
                and keep the layout uncluttered. The goal is calm simplicity.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
